<!-- ProductSpecs.vue -->
<template>
    <div class="product-specs" v-if="!loading">
        <!-- 规格分类 -->
        <div v-for="(spec, index) in specifications" :key="index" class="spec-group">
            <div class="spec-name">{{ spec.name }}</div>
            <div class="spec-values">
                <span 
                    v-for="(value, vIndex) in spec.values" 
                    :key="vIndex" 
                    class="spec-value"
                    :class="getSpecClass(spec.name, value)" 
                    @click="handleSpecSelect(spec.name, value)">
                    {{ value }}
                </span>
            </div>
        </div>

        <!-- 当前选中的规格显示 -->
        <!-- <div class="selected-info" v-if="Object.keys(selectedSpecs).length">
            <div v-for="(value, name) in selectedSpecs" :key="name">
                {{ name }}：{{ value }}
            </div>
        </div> -->

        <!-- 数量和购买区域 -->
        <!-- <div class="buy-section" v-if="currentSku">
            <div class="price-info">
                <span class="price">¥{{ currentSku.price.toFixed(2) }}</span>
                <span class="original-price" v-if="currentSku.originalPrice">
                    ¥{{ currentSku.originalPrice.toFixed(2) }}
                </span>
            </div>
            <div class="stock-info">
                库存 {{ currentSku.stock }} 件
            </div>
            <button class="buy-btn" @click="handleBuy" :disabled="!currentSku || currentSku.stock === 0">
                立即购买
            </button>
        </div> -->
    </div>

    <div v-else class="loading">
        加载中...
    </div>
</template>

<script>
export default {
    name: 'ProductSpecs',

    props: {
        productId: {
            type: [String, Number],
            required: true
        },
        specConfig: {
            type: [String, Object],
            default: null
        }
    },

    data() {
        return {
            specifications: [],        // 规格列表
            skuList: [],               // SKU列表
            selectedSpecs: {},         // 当前选中的规格
            currentSku: null,          // 当前选中的SKU
            loading: true
        }
    },

    computed: {
        // 解析后的规格配置数据
        parsedSpecConfig() {
            if (!this.specConfig) return null
            
            // 如果是字符串，则解析JSON
            if (typeof this.specConfig === 'string') {
                try {
                    return JSON.parse(this.specConfig)
                } catch (error) {
                    console.error('解析 specConfig 失败:', error)
                    return null
                }
            }
            
            // 如果已经是对象，直接返回
            return this.specConfig
        }
    },

    watch: {
        // 监听 specConfig 变化，重新加载数据
        specConfig: {
            handler() {
                if (this.parsedSpecConfig) {
                    this.loadFromSpecConfig()
                }
            },
            immediate: true,
            deep: true
        }
    },

    methods: {
        // 从 specConfig 加载规格数据
        loadFromSpecConfig() {
            const config = this.parsedSpecConfig
            if (!config) {
                console.warn('specConfig 数据为空')
                this.loading = false
                return
            }
            
            try {
                // 提取规格列表和 SKU 列表
                this.specifications = config.specifications || []
                this.skuList = config.skuList || []
                
                // 设置默认选中的规格（默认选中每个规格的第一个值）
                const defaultSpecs = {}
                this.specifications.forEach(spec => {
                    if (spec.values && spec.values.length > 0) {
                        defaultSpecs[spec.name] = spec.values[0]
                    }
                })
                this.selectedSpecs = defaultSpecs
                
                // 更新当前选中的 SKU
                this.updateCurrentSku()
                
                console.log('规格数据加载成功:', {
                    specifications: this.specifications,
                    skuList: this.skuList,
                    defaultSpecs: this.selectedSpecs
                })
            } catch (error) {
                console.error('加载规格数据失败:', error)
            } finally {
                this.loading = false
            }
        },
        
        // 加载商品规格（如果还需要从API加载，保留此方法）
        async loadProductSpecs() {
            this.loading = true
            try {
                // 优先使用 specConfig 数据
                if (this.parsedSpecConfig) {
                    this.loadFromSpecConfig()
                    return
                }
                
                // 如果没有 specConfig，则从 API 加载
                const mockApi = await import('./mockApi')
                const res = await mockApi.default.getProductSpecs(this.productId)
                if (res.code === 200) {
                    this.specifications = res.data.specifications
                    this.skuList = res.data.skuList
                    this.selectedSpecs = { ...res.data.defaultSelected }
                    this.updateCurrentSku()
                }
            } catch (error) {
                console.error('加载规格失败:', error)
                this.$emit('error', error)
            } finally {
                this.loading = false
            }
        },

        // 更新当前选中的SKU
        updateCurrentSku() {
            this.currentSku = this.findSkuBySpecs(this.selectedSpecs);
            // 触发规格变化事件
            this.$emit('spec-change', {
                specs: this.selectedSpecs,
                sku: this.currentSku
            })
        },

        // 根据规格查找对应的 SKU
        findSkuBySpecs(specs) {
            if (!this.skuList.length || !specs || !Object.keys(specs).length) {
                return null
            }
            
            return this.skuList.find(sku => {
                // 检查 SKU 的规格是否完全匹配
                return Object.entries(specs).every(([specName, specValue]) => {
                    return sku.spec[specName] === specValue
                })
            })
        },

        // 处理规格选择
        handleSpecSelect(specName, value) {
            // 检查该规格组合是否可用
            const tempSpecs = { ...this.selectedSpecs, [specName]: value }
            const sku = this.findSkuBySpecs(tempSpecs)

            // 如果该规格组合不存在或库存为0，则不可选
            if (!sku || sku.stock === 0) {
                return
            }

            // 更新选中的规格
            this.selectedSpecs = tempSpecs
            this.updateCurrentSku()
            
            // 触发规格变化事件
            this.$emit('spec-change', {
                specs: this.selectedSpecs,
                sku: this.currentSku
            })
        },

        // 获取规格项的样式类
        getSpecClass(specName, value) {
            const tempSpecs = { ...this.selectedSpecs, [specName]: value }
            const sku = this.findSkuBySpecs(tempSpecs)

            return {
                'selected': this.selectedSpecs[specName] === value,
                'disabled': !sku || sku.stock === 0
            }
        },

        // 处理购买
        handleBuy() {
            if (!this.currentSku || this.currentSku.stock === 0) return

            // 触发购买事件，将选中的SKU信息传递给父组件
            this.$emit('buy', {
                sku: this.currentSku,
                specs: this.selectedSpecs
            })
        },
        
        // 重置选中规格
        resetSelection() {
            if (this.specifications.length) {
                const defaultSpecs = {}
                this.specifications.forEach(spec => {
                    if (spec.values && spec.values.length) {
                        defaultSpecs[spec.name] = spec.values[0]
                    }
                })
                this.selectedSpecs = defaultSpecs
                this.updateCurrentSku()
            }
        }
    }
}
</script>

<style scoped>
/* 保持原有样式不变，添加一些优化 */

.product-specs {
width: 100%;
    padding: 16px 0 0 0;
    background: #fff;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
}

.spec-group {
    margin-bottom: 24px;
}

.spec-name {
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
    font-weight: 500;
}

.spec-values {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}

.spec-value {
    min-width: 68px;
    height: 40px;
    padding: 0 16px;
    border: 1px solid #e5e5e5;
    border-radius: 20px;
    background: #fff;
    color: #333;
    font-size: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    box-sizing: border-box;
}

/* 选中状态 */
.spec-value.selected {
    border-color: #07c160;
    color: #07c160;
    background: #f0fff5;
}

/* 禁用状态（无库存） */
.spec-value.disabled {
    border-color: #f0f0f0;
    color: #ccc;
    background: #fafafa;
    cursor: not-allowed;
    text-decoration: line-through;
}

/* 选中且禁用状态 */
.spec-value.selected.disabled {
    border-color: #ff4444;
    color: #ff4444;
    background: #fff0f0;
}

.selected-info {
    margin: 20px 0;
    padding: 14px 16px;
    background: #f8f8f8;
    border-radius: 8px;
    font-size: 14px;
    color: #333;
    line-height: 1.5;
}

.selected-info > div {
    margin-bottom: 4px;
}

.selected-info > div:last-child {
    margin-bottom: 0;
}

.buy-section {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid #f0f0f0;
}

.price-info {
    display: flex;
    align-items: baseline;
    gap: 8px;
    margin-bottom: 8px;
}

.price {
    font-size: 24px;
    font-weight: 600;
    color: #ff4444;
}

.original-price {
    font-size: 14px;
    color: #999;
    text-decoration: line-through;
}

.stock-info {
    font-size: 14px;
    color: #666;
    margin-bottom: 16px;
}

.buy-btn {
    width: 100%;
    height: 48px;
    background: linear-gradient(135deg, #07c160, #06b85c);
    color: #fff;
    border: none;
    border-radius: 24px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 8px rgba(7, 193, 96, 0.3);
}

.buy-btn:active {
    transform: translateY(1px);
    box-shadow: 0 1px 4px rgba(7, 193, 96, 0.3);
}

.buy-btn:disabled {
    background: #ccc;
    background: linear-gradient(135deg, #ccc, #bbb);
    box-shadow: none;
    cursor: not-allowed;
    transform: none;
}

.loading {
    padding: 40px;
    text-align: center;
    color: #999;
    font-size: 14px;
}
</style>